import React, { useState, useMemo } from 'react';
import { Calendar, TrendingUp, Zap, Users, Shield, Globe, Filter, Download, Clock } from 'lucide-react';
import { marketAnalysis } from '../data/market-analysis';
import { exportChart } from '../utils/visualization';
import type { FilterOptions, ExportConfig } from '../types/visualization';

interface MarketTrendsTimelineProps {
  filters?: FilterOptions;
  onFilterChange?: (filters: FilterOptions) => void;
  className?: string;
}

interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  impact: 'high' | 'medium' | 'low';
  category: 'technology' | 'market' | 'regulation' | 'competition' | 'investment';
  affectedCompanies: string[];
  status: 'completed' | 'ongoing' | 'projected';
}

export const MarketTrendsTimeline: React.FC<MarketTrendsTimelineProps> = ({
  filters = {},
  onFilterChange,
  className = '',
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedImpact, setSelectedImpact] = useState<string>('');
  const [timeRange, setTimeRange] = useState<'all' | 'current' | 'future'>('all');

  // Generate timeline events from market analysis data
  const timelineEvents: TimelineEvent[] = useMemo(() => {
    const events: TimelineEvent[] = [];

    // Add trend-based events
    marketAnalysis.trends.forEach((trend, index) => {
      const [startYear] = trend.timeline.includes('-') 
        ? trend.timeline.split('-').map(year => parseInt(year.trim()))
        : [2024, 2025];

      events.push({
        id: `trend-${index}`,
        title: trend.trend_name,
        description: trend.description,
        date: `${startYear}-01-01`,
        impact: trend.impact_level,
        category: trend.trend_name.toLowerCase().includes('ai') ? 'technology' :
                 trend.trend_name.toLowerCase().includes('platform') ? 'market' :
                 trend.trend_name.toLowerCase().includes('retention') ? 'market' :
                 trend.trend_name.toLowerCase().includes('insurance') ? 'regulation' :
                 trend.trend_name.toLowerCase().includes('expansion') ? 'market' :
                 'competition',
        affectedCompanies: trend.affected_companies,
        status: (startYear || 2024) <= 2024 ? 'ongoing' : 'projected',
      });
    });

    // Add historical milestone events
    const milestoneEvents: TimelineEvent[] = [
      {
        id: 'angi-spinoff',
        title: 'Angi Inc. Spin-off Announced',
        description: 'ANGI Homeservices announces planned spin-off and strategic restructuring to focus on core marketplace business.',
        date: '2024-01-15',
        impact: 'high',
        category: 'competition',
        affectedCompanies: ['angi'],
        status: 'ongoing',
      },
      {
        id: 'thumbtack-series-g',
        title: 'Thumbtack Series G Funding',
        description: 'Thumbtack raises $275M Series G funding round at $3.2B valuation, led by Tiger Global.',
        date: '2021-07-01',
        impact: 'high',
        category: 'investment',
        affectedCompanies: ['thumbtack'],
        status: 'completed',
      },
      {
        id: 'taskrabbit-ikea-acquisition',
        title: 'IKEA Acquires TaskRabbit',
        description: 'IKEA completes acquisition of TaskRabbit to integrate assembly services with furniture sales.',
        date: '2017-09-01',
        impact: 'high',
        category: 'competition',
        affectedCompanies: ['taskrabbit'],
        status: 'completed',
      },
      {
        id: 'handy-angi-acquisition',
        title: 'Angi Acquires Handy',
        description: 'ANGI Homeservices acquires Handy for $165.5M to expand on-demand services portfolio.',
        date: '2018-10-11',
        impact: 'medium',
        category: 'competition',
        affectedCompanies: ['handy', 'angi'],
        status: 'completed',
      },
      {
        id: 'ai-integration-2025',
        title: 'AI-First Marketplace Evolution',
        description: 'Major platforms achieve 3.7x ROI improvements through advanced AI matching algorithms and demand forecasting.',
        date: '2025-06-01',
        impact: 'high',
        category: 'technology',
        affectedCompanies: ['thumbtack', 'angi', 'porch_group'],
        status: 'projected',
      },
      {
        id: 'market-consolidation-2025',
        title: 'Industry Consolidation Accelerates',
        description: 'Predicted wave of M&A activity as platforms seek scale advantages and market consolidation.',
        date: '2025-01-01',
        impact: 'high',
        category: 'competition',
        affectedCompanies: ['angi', 'porch_group'],
        status: 'projected',
      },
    ];

    events.push(...milestoneEvents);

    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, []);

  const filteredEvents = useMemo(() => {
    return timelineEvents.filter(event => {
      if (selectedCategory && event.category !== selectedCategory) return false;
      if (selectedImpact && event.impact !== selectedImpact) return false;
      if (filters.company && !event.affectedCompanies.includes(filters.company)) return false;
      
      const eventYear = new Date(event.date).getFullYear();
      if (timeRange === 'current' && (eventYear < 2024 || eventYear > 2024)) return false;
      if (timeRange === 'future' && eventYear <= 2024) return false;
      
      return true;
    });
  }, [timelineEvents, selectedCategory, selectedImpact, filters.company, timeRange]);

  const handleExport = async (format: 'png' | 'csv') => {
    const chartElement = document.getElementById('market-trends-timeline');
    if (!chartElement) return;

    const config: ExportConfig = {
      format,
      filename: 'market-trends-timeline',
      includeData: true,
    };

    await exportChart(config, chartElement, filteredEvents);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'technology': return Zap;
      case 'market': return TrendingUp;
      case 'regulation': return Shield;
      case 'competition': return Users;
      case 'investment': return Calendar;
      default: return Globe;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'technology': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'market': return 'bg-green-100 text-green-800 border-green-200';
      case 'regulation': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'competition': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'investment': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'border-red-400 bg-red-50';
      case 'medium': return 'border-yellow-400 bg-yellow-50';
      case 'low': return 'border-green-400 bg-green-50';
      default: return 'border-gray-400 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✓';
      case 'ongoing': return '○';
      case 'projected': return '△';
      default: return '?';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Market Trends Timeline</h2>
          <p className="text-sm text-gray-600 mt-1">
            Key events, trends, and projections shaping the local service marketplace
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setTimeRange('all')}
              className={`px-3 py-1 text-sm rounded ${
                timeRange === 'all'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Time
            </button>
            <button
              onClick={() => setTimeRange('current')}
              className={`px-3 py-1 text-sm rounded ${
                timeRange === 'current'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              2024
            </button>
            <button
              onClick={() => setTimeRange('future')}
              className={`px-3 py-1 text-sm rounded ${
                timeRange === 'future'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Future
            </button>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleExport('png')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as PNG"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as CSV"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm font-medium text-blue-900 mb-1">Total Events</div>
          <div className="text-2xl font-bold text-blue-900">{filteredEvents.length}</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-sm font-medium text-red-900 mb-1">High Impact</div>
          <div className="text-2xl font-bold text-red-900">
            {filteredEvents.filter(e => e.impact === 'high').length}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm font-medium text-green-900 mb-1">Ongoing</div>
          <div className="text-2xl font-bold text-green-900">
            {filteredEvents.filter(e => e.status === 'ongoing').length}
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm font-medium text-purple-900 mb-1">Projected</div>
          <div className="text-2xl font-bold text-purple-900">
            {filteredEvents.filter(e => e.status === 'projected').length}
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                value={filters.company || ''}
                onChange={(e) => onFilterChange?.({ ...filters, company: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Companies</option>
                <option value="thumbtack">Thumbtack</option>
                <option value="taskrabbit">TaskRabbit</option>
                <option value="angi">Angi</option>
                <option value="handy">Handy</option>
                <option value="porch_group">Porch Group</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="technology">Technology</option>
                <option value="market">Market</option>
                <option value="regulation">Regulation</option>
                <option value="competition">Competition</option>
                <option value="investment">Investment</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impact Level</label>
              <select
                value={selectedImpact}
                onChange={(e) => setSelectedImpact(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Impact Levels</option>
                <option value="high">High Impact</option>
                <option value="medium">Medium Impact</option>
                <option value="low">Low Impact</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as 'all' | 'current' | 'future')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="current">Current (2024)</option>
                <option value="future">Future (2025+)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div id="market-trends-timeline" className="relative">
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300" />
        
        <div className="space-y-6">
          {filteredEvents.map((event) => {
            const CategoryIcon = getCategoryIcon(event.category);
            
            return (
              <div key={event.id} className="relative flex items-start gap-6">
                {/* Timeline dot */}
                <div className={`flex-shrink-0 w-16 h-16 rounded-full border-4 ${getImpactColor(event.impact)} flex items-center justify-center relative z-10`}>
                  <CategoryIcon className="h-6 w-6 text-gray-700" />
                </div>

                {/* Event content */}
                <div className="flex-1 min-w-0">
                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    {/* Event header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCategoryColor(event.category)}`}>
                            {event.category}
                          </span>
                          <span className="text-sm text-gray-500">
                            {getStatusIcon(event.status)} {event.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <Clock className="h-4 w-4" />
                          {formatDate(event.date)}
                          <span className="text-gray-400">•</span>
                          <span className={`font-medium ${
                            event.impact === 'high' ? 'text-red-600' :
                            event.impact === 'medium' ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {event.impact} impact
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Event description */}
                    <p className="text-gray-700 mb-4">{event.description}</p>

                    {/* Affected companies */}
                    {event.affectedCompanies.length > 0 && (
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Affected:</span>
                        <div className="flex flex-wrap gap-2">
                          {event.affectedCompanies.map((company) => (
                            <span 
                              key={company}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                            >
                              {company.charAt(0).toUpperCase() + company.slice(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs font-medium text-gray-600 mb-2">Status</div>
            <div className="space-y-1 text-xs text-gray-700">
              <div>✓ Completed</div>
              <div>○ Ongoing</div>
              <div>△ Projected</div>
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-600 mb-2">Impact Level</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border-2 border-red-400 bg-red-50" />
                High Impact
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border-2 border-yellow-400 bg-yellow-50" />
                Medium Impact
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border-2 border-green-400 bg-green-50" />
                Low Impact
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-600 mb-2">Categories</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <Zap className="h-3 w-3 text-blue-600" />
                Technology
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3 w-3 text-green-600" />
                Market
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-3 w-3 text-purple-600" />
                Competition
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};